import { GraduationCap, Target, Users } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { DonateButton } from "@/components/DonateButton";
import { IMAGES } from "@/data/content";

const CAMP_PHOTOS = [
  {
    src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_edupower-foundation/artifacts/8145566fd632e9cd_WhatsApp%20Image%202026-06-14%20at%2019.52.03%20%281%29.jpeg",
    alt: "Learners arriving at the 2026 Grade 12 English Camp",
    caption: "Ready for the Final Lap",
  },
  {
    src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_edupower-foundation/artifacts/ed3078d6a4b9d6f5_WhatsApp%20Image%202026-06-14%20at%2019.52.03%20%282%29.jpeg",
    alt: "The Final Lap camp crew in the mountains",
    caption: "Camp crew in the mountains",
  },
  {
    src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_edupower-foundation/artifacts/8f79687c6cd2a3f0_WhatsApp%20Image%202026-06-14%20at%2019.52.03.jpeg",
    alt: "The Final Lap class of 2026 at sunset",
    caption: "Golden hour at camp",
  },
  {
    src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_edupower-foundation/artifacts/edfcc72e41f08497_WhatsApp%20Image%202026-06-14%20at%2019.53.10%20%282%29.jpeg",
    alt: "Learners meeting a horse during the camp outdoor programme",
    caption: "New friends on the trail",
  },
  {
    src: "https://customer-assets-gfyr7b9c.emergentagent.net/job_edupower-foundation/artifacts/0491db9be98313a7_WhatsApp%20Image%202026-06-14%20at%2019.53.12%20%281%29.jpeg",
    alt: "A learner stepping off the bus at camp",
    caption: "Stepping off the bus",
  },
];

const PILLARS = [
  {
    icon: Target,
    title: "Focused intervention",
    text: "We focus on the exact areas learners struggle with most, sharpening the quality of their work and their marks.",
  },
  {
    icon: Users,
    title: "Teacher-led mentorship",
    text: "Teachers mentor learners directly, the people who know the classroom best, guiding the learners who need it most.",
  },
  {
    icon: GraduationCap,
    title: "District-wide reach",
    text: "Thirteen schools in the Metro South district, taken under the wing of the English department.",
  },
];

export default function EduPower13() {
  return (
    <div data-testid="edupower13-page">
      <section className="relative overflow-hidden bg-teal grain py-16 sm:py-20">
        <div className="absolute -right-32 top-0 h-96 w-96 rounded-full bg-sand/10 blur-[120px]" />
        <div className="relative mx-auto max-w-5xl px-6">
          <Reveal>
            <Eyebrow light>Academic mentorship</Eyebrow>
            <h1 className="mt-5 font-display text-5xl font-extrabold tracking-tight text-sand sm:text-6xl lg:text-7xl">
              EduPower<span className="text-ember">13</span>
            </h1>
            <p className="mt-7 max-w-3xl text-base leading-relaxed text-sand/80 md:text-lg">
              Thirteen schools in the Metro South district, taken under the wing of the English
              department. We focus on the areas learners struggle with, teachers mentoring learners,
              raising the quality of their work and their marks, one school at a time.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid gap-5 md:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div
                  className="h-full rounded-[2rem] bg-white p-8 shadow-[0_14px_44px_-24px_rgba(13,124,133,0.4)]"
                  data-testid={`edupower13-pillar-${i}`}
                >
                  <span className="inline-flex h-13 w-13 items-center justify-center rounded-2xl bg-teal-subtle p-3 text-teal">
                    <p.icon size={24} />
                  </span>
                  <h3 className="mt-5 font-heading text-xl font-bold text-coal">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-coal-muted">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div className="mt-16 grid items-center gap-10 overflow-hidden rounded-[2.5rem] bg-coal-dark grain lg:grid-cols-2">
              <div className="p-10 lg:p-14">
                <span className="font-display text-6xl font-extrabold text-ember sm:text-7xl">13</span>
                <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-sand">
                  schools. One standard: better.
                </h2>
                <p className="mt-4 text-sm leading-relaxed text-sand/65 sm:text-base">
                  When a whole district's English department wraps around its schools, struggling
                  learners stop falling through the cracks. EduPower13 is that wrap, structured,
                  sustained and personal.
                </p>
                <div className="mt-8">
                  <DonateButton testId="edupower13-donate-button">Support a school</DonateButton>
                </div>
              </div>
              <img
                src={IMAGES.hero}
                alt="Learners working together in class"
                className="h-full max-h-[30rem] w-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-sand py-16" data-testid="edupower13-camp-gallery">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <Eyebrow>Final Lap 2026</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-coal sm:text-4xl">
              Inside the <span className="text-teal">Grade 12 camp</span>
            </h2>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-coal-muted">
              Study sessions, mountain air and new friendships, scenes from the 2026 Final Lap camp.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {CAMP_PHOTOS.map((photo, i) => (
              <Reveal key={photo.src} delay={(i % 3) * 0.08}>
                <figure
                  className="group relative overflow-hidden rounded-[2rem] shadow-[0_16px_44px_-22px_rgba(26,29,36,0.45)]"
                  data-testid={`camp-photo-${i}`}
                >
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    loading="lazy"
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-coal-dark/70 via-transparent to-transparent" />
                  <figcaption className="absolute inset-x-0 bottom-0 p-5 font-heading text-sm font-bold text-sand">
                    {photo.caption}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
